// ProductionCycleTimeline.js - visual timeline of a ProductionOrder's full
// journey, so staff can see where a run stands without hopping between
// Produits / Matières premières / Production / Commandes pages.
// Two variants: "full" (a card with the prep tier, the 8-step production
// stepper, and a separated "Livraison client" tier) and "compact" (a
// segmented mini progress bar for table rows). Approved mockup: see the
// "Production Cycle Timeline" design canvas.
import React from "react";
import { STATUS_ORDER, HANDOFF_STATUS } from "../../Data/productionStatuses";
import "./ProductionCycleTimeline.css";

const CheckIcon = ({ size = 16, color = "#fff" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const TruckIcon = ({ size = 17, color = "#94a3b8" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7h11v10H3z" />
    <path d="M14 10h4l3 3v4h-7z" />
    <circle cx="7.5" cy="19.5" r="1.5" />
    <circle cx="17.5" cy="19.5" r="1.5" />
  </svg>
);

// Full-card variant. `order` is the populated Order document linked via
// ProductionOrder.orderId ({_id, status, company, number, total, city,
// createdAt}), or null for bulk/speculative production with no client
// order attached.
export const ProductionCycleTimelineCard = ({ status, hasDesign, order }) => {
  const currentIndex = STATUS_ORDER.indexOf(status);
  const hasOrder = !!order;

  const prep = [
    { label: "Produit créé", done: true },
    { label: "Modèle créé", done: hasDesign, skipped: !hasDesign },
    { label: "Stock MP réservé", done: true },
  ];

  const delivered = hasOrder && order.status === "livré";

  return (
    <div className="pct-card">
      <div className="pct-tier-label">Préparation</div>
      <div className="pct-prep-row">
        {prep.map((p, i) => (
          <div className="pct-prep-item" key={p.label}>
            <div className={`pct-prep-dot${p.skipped ? " pct-prep-dot--skipped" : ""}`}>
              {p.done && <CheckIcon size={12} color={p.skipped ? "#cbd5e1" : "#16a34a"} />}
            </div>
            <span className={`pct-prep-label${p.skipped ? " pct-prep-label--skipped" : ""}`}>{p.label}</span>
            {i < prep.length - 1 && <div className="pct-prep-connector" />}
          </div>
        ))}
      </div>

      <div className="pct-tier-label" style={{ marginTop: 18 }}>Production</div>
      <div className="pct-stepper">
        {STATUS_ORDER.map((s, i) => {
          const done = i < currentIndex;
          const current = i === currentIndex;
          const upcoming = i > currentIndex;
          const isHandoff = s === HANDOFF_STATUS;
          return (
            <div className="pct-step" key={s}>
              <div className={`pct-node-wrap${current ? " pct-node-wrap--pulse" : ""}`}>
                <div className={`pct-node${done ? " pct-node--done" : ""}${current ? " pct-node--current" : ""}${upcoming ? " pct-node--upcoming" : ""}`}>
                  {done && <CheckIcon size={16} />}
                  {current && <span className="pct-node-dot" />}
                  {upcoming && <span className="pct-node-index">{i + 1}</span>}
                </div>
              </div>
              <div className="pct-step-label-wrap">
                <div className={`pct-step-label${current ? " pct-step-label--current" : ""}${done ? " pct-step-label--done" : ""}`}>{s}</div>
                {isHandoff && <div className="pct-tag">Autre atelier</div>}
              </div>
              {i < STATUS_ORDER.length - 1 && (
                <div className={`pct-connector${done ? " pct-connector--done" : ""}`} />
              )}
            </div>
          );
        })}
      </div>

      <div className="pct-divider" />

      <div className={`pct-delivery${delivered ? " pct-delivery--done" : ""}`}>
        <div className="pct-delivery-left">
          <div className="pct-delivery-icon">
            {delivered ? <CheckIcon size={17} color="#16a34a" /> : <TruckIcon />}
          </div>
          <div>
            <div className="pct-delivery-title">Livraison client</div>
            <div className="pct-delivery-sub">
              {hasOrder
                ? "Étape finale de la commande — distincte du « Livré » ci-dessus"
                : "Production libre (sans commande liée)"}
            </div>
            {hasOrder && (
              <div className="pct-delivery-order-info">
                {order.company} · Commande n° {order.number}
                {order.city ? ` · ${order.city}` : ""}
                {typeof order.total === "number" ? ` · ${order.total.toLocaleString("fr-FR")} €` : ""}
                {order.createdAt ? ` · ${new Date(order.createdAt).toLocaleDateString("fr-FR")}` : ""}
              </div>
            )}
          </div>
        </div>
        {hasOrder && (
          <div className={`pct-delivery-badge${delivered ? " pct-delivery-badge--done" : ""}`}>
            {delivered ? "Livré" : "En attente"}
          </div>
        )}
      </div>
    </div>
  );
};

// Compact variant for table rows: a segmented progress bar.
export const ProductionCycleTimelineCompact = ({ status }) => {
  const currentIndex = STATUS_ORDER.indexOf(status);
  const doneCount = Math.max(0, currentIndex);
  const isTerminal = currentIndex === STATUS_ORDER.length - 1;
  const isHandoff = status === HANDOFF_STATUS;

  return (
    <div className="pct-compact">
      <div className="pct-compact-segments">
        {STATUS_ORDER.map((s, i) => (
          <div key={s} className={`pct-compact-segment${i < doneCount || (i === currentIndex && isTerminal) ? " pct-compact-segment--done" : ""}`} />
        ))}
      </div>
      <div className="pct-compact-label-row">
        {isTerminal && <CheckIcon size={12} color="#16a34a" />}
        {isHandoff && <span className="pct-compact-dot" />}
        <span className={`pct-compact-label${isTerminal ? " pct-compact-label--done" : ""}${isHandoff ? " pct-compact-label--handoff" : ""}`}>{status}</span>
      </div>
    </div>
  );
};
